import React from 'react';
import classNames from 'classnames';
import Overlay from 'ui/utils/Overlay';

import './Dialog.scss';

function DialogHeader({ title, onDrag, onClose }) {
  const [capture, setCapture] = React.useState(false);
  const nodeRef = React.useRef();

  const onPointerDown = React.useCallback(e => {
    if (e.target === nodeRef.current) {
      nodeRef.current.setPointerCapture(e.pointerId);
      setCapture(true);
      if (onDrag) onDrag({x: e.clientX, y: e.clientY});
    }
  }, [onDrag]);

  const onPointerUp = React.useCallback(e => {
    nodeRef.current.releasePointerCapture(e.pointerId);
    setCapture(false);
    if (onDrag) onDrag(null);
  }, [onDrag]);

  const onPointerMove = React.useCallback(e => {
    if (onDrag) onDrag({x: e.clientX, y: e.clientY});
  }, [onDrag]);

  return (
    <div className="ui-DialogHeader" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerMove={capture ? onPointerMove : null} ref={nodeRef}>
      <span className="ui-DialogTitle">
        {title}
      </span>
      {!!onClose && (
        <span className="ui-DialogClose" onClick={onClose}/>
      )}
    </div>
  );
}

export default function Dialog({ modal, title, children, onClose, style, className, onKeyDown: onKeyDownOriginal, ...props }) {
  const [pos, setPos] = React.useState(null);
  const dragRef = React.useRef();
  const nodeRef = React.useRef();

  const onDrag = React.useCallback(pos => {
    if (pos) {
      const rect = nodeRef.current.getBoundingClientRect();
      const width = document.documentElement.clientWidth - 10;
      const height = document.documentElement.clientHeight - 10;
      if (dragRef.current) {
        const left = Math.min(Math.max(0, Math.round(pos.x - dragRef.current.x)), width - rect.right + rect.left);
        const top = Math.min(Math.max(0, Math.round(pos.y - dragRef.current.y)), height - rect.bottom + rect.top);
        setPos({left, top});
      } else {
        dragRef.current = {x: pos.x - rect.left, y: pos.y - rect.top};
      }
    } else {
      dragRef.current = null;
    }
  }, []);

  React.useLayoutEffect(() => {
    function resize() {
      const rect = nodeRef.current.getBoundingClientRect();
      const width = document.documentElement.clientWidth - 10;
      const height = document.documentElement.clientHeight - 10;
      setPos({left: Math.round((width - rect.right + rect.left) / 2), top: Math.round((height - rect.bottom + rect.top) / 2)});
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const isRendered = pos != null;
  React.useLayoutEffect(() => {
    if (isRendered) {
      nodeRef.current.focus();
    }
  }, [isRendered]);

  const onKeyDown = React.useCallback(e => {
    if (onClose && (e.key === "Esc" || e.key === "Escape")) {
      onClose();
    }
    if (!modal && onKeyDownOriginal) {
      onKeyDownOriginal();
    }
  }, [onClose, modal, onKeyDownOriginal]);

  style = {...style};
  if (pos) {
    style.left = pos.left;
    style.top = pos.top;
  } else {
    style.visibility = "hidden";
  }

  return (
    <Overlay modal={modal} onKeyDown={modal && onKeyDown}>
      <div className={classNames("ui-Dialog", className)} ref={nodeRef} style={style} tabIndex={-1} onKeyDown={(modal ? null : onKeyDown) || onKeyDownOriginal} {...props}>
        {!!title && <DialogHeader title={title} onClose={onClose} onDrag={onDrag}/>}
        {children}
      </div>
    </Overlay>
  );
}

Dialog.Content = ({ children }) => <div className="ui-DialogContent">{children}</div>;
Dialog.Footer = ({ children }) => <div className="ui-DialogFooter">{children}</div>;
